import React, { useContext } from "react";
import { Handle, Position } from "reactflow";
import { Flex, Tag, Typography } from "antd";
import { PhoneOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { AppContext } from "../../../context/AppContext";
import { conditionCodeToTitle, getConditionIconAndClass } from "../utils/conditionCodeMapping";
import { formatPhoneNumber } from "../../utils/commonUtils";

const { Text } = Typography;

function getServiceIcon(phoneNumber: string, appProperties: { savedNumbers?: Array<{ phoneNumber?: string; integId?: string }>; installedApps?: Array<{ integProps?: { integId?: string }; right?: { service_name?: string } }> } | null): React.ReactNode {
    const saved = appProperties?.savedNumbers?.find((n) => n?.phoneNumber === phoneNumber);
    const serviceName = appProperties?.installedApps?.find((obj) => obj?.integProps?.integId === saved?.integId)?.right?.service_name;
    if (serviceName) {
        return <div className={`hs-scale-08 numberlist-sprites numberlist-${serviceName}`} />;
    }
    return <PhoneOutlined />;
}

const DEFAULT_NODE_ICON = { icon: <div className="automationIconsSprite select-icon" /> };

function getNodeIcon(data: ConditionNodeData) {
    if (!data.conditionCode) return DEFAULT_NODE_ICON;
    const { icon } = getConditionIconAndClass(data.conditionCode);
    return { icon };
}

export interface BusinessHoursDayConfig {
    hourStartTime?: string;
    minuteStartTime?: string;
    hourEndTime?: string;
    minuteEndTime?: string;
    isOpen?: number;
    businessDay?: string;
}

export interface BusinessHoursConfig {
    timeZone?: string;
    businessDays?: BusinessHoursDayConfig[];
}

export interface ConditionNodeData {
    label?: string;
    title?: string;
    conditionCode?: string;
    phoneNumbers?: string[];
    keywords?: string[];
    matchType?: string;
    countries?: string[];
    runSchedule?: string;
    businessHours?: BusinessHoursConfig;
    isPlaceholder?: boolean;
    source?: string;
    deliveryStatus?: string[];
    applyOncePerContact?: boolean;
}

const ConditionNodeComponent: React.FC<{ data: ConditionNodeData & { isOutgoing?: boolean }; selected?: boolean }> = ({ data, selected }) => {
    const [appProperties] = useContext(AppContext);
    const hasTypeSelected = !!data.conditionCode;
    const isPlaceholder = data.isPlaceholder ?? !hasTypeSelected;
    const iconMeta = getNodeIcon(data);

    const displayTitle = isPlaceholder
        ? "Select a Condition..."
        : (data.title ?? conditionCodeToTitle(data.conditionCode) ?? "When to continue");

    return (
        <div
            className={`hs-w-350-px position-relative hs-border-grey rounded-4 p-3 ${isPlaceholder ? "trigger-rule-node-placeholder hs-border-color-placeholder bg-transparent" : "hs-bg-plain-white"} ${selected ? "hs-border-violet" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={`Condition: ${displayTitle}. ${selected ? "Selected" : "Click to select"}`}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    (e.currentTarget as HTMLElement).click();
                }
            }}
        >
            <Handle type="target" position={Position.Top} id="target" className="opacity-0"/>
            <Handle type="source" position={Position.Bottom} id="source" className="opacity-0"/>
            <span className={`trigger-rule-node-tag hs-fs-11 hs-fw-600 rounded-3 border-1 hs-border-violet py-1 px-3 ${isPlaceholder ? "tag-fill" : "hs-color-violet hs-bg-plain-white"}`}>{isPlaceholder ? "+ NEW STEP" : "Condition"}</span>

            {isPlaceholder ? (
                <div className="d-flex align-items-center gap-2">
                    <div className="d-flex align-items-center justify-content-center">
                        {iconMeta.icon}
                    </div>
                    <div className="d-flex flex-column gap-1 flex-grow-1 min-w-0">
                        <span className="hs-fw-600 hs-color-BlackRussian hs-fs-12">{displayTitle}</span>
                        <span className="hs-fs-12 hs-color-medium-dark">Choose from the panel on the right</span>
                    </div>
                </div>
            ) : (
                <>
                    <div className=" d-flex align-items-center gap-2">
                        <div className="d-flex align-items-center justify-content-center">
                            {iconMeta.icon}
                        </div>
                        <span className="hs-fw-600 hs-color-BlackRussian hs-fs-12">{displayTitle}</span>
                    </div>
                    <Flex vertical gap="small" justify="space-between" className="pt-3">
                    {data.phoneNumbers && data.phoneNumbers.length > 0 && (
                        <div className="d-flex flex-wrap gap-1 mw-100">
                            {data.phoneNumbers.map((phoneNumber) => (
                                <Tag
                                    key={phoneNumber}
                                    icon={getServiceIcon(phoneNumber, appProperties)}
                                    title={phoneNumber}
                                    className="d-inline-flex align-items-center hs-fs-12 hs-fw-400 hs-color-dark-grey hs-bg-pale-blue-1 hs-border-md-grey rounded-2 m-0 mw-100 hs-text-truncate"
                                >
                                    {formatPhoneNumber(phoneNumber)}
                                </Tag>
                            ))}
                        </div>
                    )}
                    {data.keywords && data.keywords.length > 0 && (
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <Text className="hs-fs-12 hs-fw-400 hs-color-BlackRussian flex-shrink-0">Keywords:</Text>
                            <div className="d-flex flex-wrap gap-1 mw-100">
                                {data.keywords.map((kw) => (
                                    <Tag
                                        key={kw}
                                        title={kw}
                                        className="hs-fs-12 hs-fw-400 hs-color-dark-grey hs-bg-pale-blue-1 hs-border-md-grey rounded-2 m-0 mw-100 hs-text-truncate"
                                    >
                                        {kw}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    )}
                    {data.matchType && (
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <Text className="hs-fs-12 hs-fw-400 hs-color-BlackRussian flex-shrink-0">Match:</Text>
                            <Tag className="hs-fs-12 hs-fw-400 hs-color-dark-grey hs-bg-pale-blue-1 hs-border-md-grey rounded-2 m-0">
                                {data.matchType === "StartsWith"
                                    ? "Starts With"
                                    : data.matchType === "Contains"
                                      ? "Contains"
                                      : data.matchType === "Exact"
                                        ? "Exact"
                                        : data.matchType}
                            </Tag>
                        </div>
                    )}
                    {data.countries && data.countries.length > 0 && (
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <Text className="hs-fs-12 hs-fw-400 hs-color-BlackRussian flex-shrink-0">Countries:</Text>
                            <div className="d-flex flex-wrap gap-1 mw-100">
                                {data.countries.map((country) => (
                                    <Tag
                                        key={country}
                                        title={country}
                                        className="hs-fs-12 hs-fw-400 hs-color-dark-grey hs-bg-pale-blue-1 hs-border-md-grey rounded-2 m-0 mw-100 hs-text-truncate"
                                    >
                                        {country}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    )}
                    {data.runSchedule && (
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <Text className="hs-fs-12 hs-fw-400 hs-color-BlackRussian flex-shrink-0">Run schedule:</Text>
                            <Tag icon={<ClockCircleOutlined />} className="hs-fs-12 hs-fw-400 hs-color-dark-grey hs-bg-pale-blue-1 hs-border-md-grey rounded-2 m-0">
                                {data.runSchedule === "BusinessHours"
                                      ? "Business Hours"
                                      : data.runSchedule === "OutsideBusinessHours"
                                        ? "Outside Business Hours"
                                        : data.runSchedule === "SpecificHours"
                                          ? "Specific Hours"
                                          : data.runSchedule}
                            </Tag>
                        </div>
                    )}
                </Flex>
                </>
            )}
        </div>
    );
};

export const ConditionNode = React.memo(ConditionNodeComponent);
